import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface MarkdownContentProps {
  path: string;
}

export function MarkdownContent({ path }: MarkdownContentProps) {
  const { t } = useTranslation();
  const [content, setContent] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    setError(false);
    setContent("");

    fetch(path)
      .then((res) => {
        if (!res.ok) throw new Error("Not found");
        return res.text();
      })
      .then(setContent)
      .catch(() => setError(true));
  }, [path]);

  if (error) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-warm-50 mb-4">{t("docs.notFound.title")}</h1>
        <p className="text-warm-500">{t("docs.notFound.description")}</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-dark-hover rounded w-3/4" />
        <div className="h-4 bg-dark-hover rounded w-full" />
        <div className="h-4 bg-dark-hover rounded w-5/6" />
        <div className="h-4 bg-dark-hover rounded w-4/6" />
      </div>
    );
  }

  return (
    <div
      className="prose prose-invert max-w-none"
      dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }}
    />
  );
}

function parseMarkdown(md: string): string {
  let html = md;

  html = html.replace(/^### (.*$)/gm, '<h3 id="$1">$1</h3>');
  html = html.replace(/^## (.*$)/gm, '<h2 id="$1">$1</h2>');
  html = html.replace(/^# (.*$)/gm, '<h1 id="$1">$1</h1>');

  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
  html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');

  html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre class="code-block"><code>$2</code></pre>');

  html = html.replace(/^\- (.*$)/gm, "<li>$1</li>");
  html = html.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");

  html = html.replace(/^\d+\. (.*$)/gm, "<li>$1</li>");

  html = html.replace(/^\|.*\|$/gm, (match) => {
    const cells = match
      .split("|")
      .filter((c) => c.trim())
      .map((c) => c.trim());
    return `<tr>${cells.map((c) => `<td>${c}</td>`).join("")}</tr>`;
  });

  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  html = html.replace(/\n\n/g, "</p><p>");
  html = `<p>${html}</p>`;
  html = html.replace(/<p><\/p>/g, "");
  html = html.replace(/<p>(<h[123][^>]*>.*<\/h[123]>)<\/p>/g, "$1");
  html = html.replace(/<p>(<ul>.*<\/ul>)<\/p>/gs, "$1");
  html = html.replace(/<p>(<pre[^>]*>.*<\/pre>)<\/p>/gs, "$1");
  html = html.replace(/<p>(<li>.*<\/li>)<\/p>/g, "$1");
  html = html.replace(/<p>(<tr>.*<\/tr>)<\/p>/g, "$1");

  html = html.replace(/id="([^"]*)"/g, (_, id) => {
    const slugId = id
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    return `id="${slugId}"`;
  });

  return html;
}
